import React, { Component, ReactNode } from "react";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { fontSize, fontWeight, spacing, dataViz } from "@atlas-int/tokens";
import { Button } from "@atlas-int/ui";
import type { ErrorLike } from "@apollo/client";
import {
  classifyError,
  getHumanReadableMessage,
  getRecoveryAction,
} from "../graphql/errors";

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: ${spacing[16]};
  text-align: center;
`;

const ErrorTitle = styled.h2`
  font-size: ${fontSize["3xl"]};
  font-weight: ${fontWeight.semibold};
  margin-bottom: ${spacing[8]};
  color: ${dataViz.textDark};
`;

const ErrorMessage = styled.p`
  font-size: ${fontSize.lg};
  color: ${dataViz.textSecondary};
  margin-bottom: ${spacing[4]};
  max-width: 40rem;
`;

const ErrorButton = styled(Button)`
  margin-top: ${spacing[12]};
  font-weight: ${fontWeight.semibold};
  text-transform: uppercase;
`;

interface Props {
  children: ReactNode;
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  lastResetKey?: string;
}

class VisualizationErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, lastResetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (state.hasError && props.resetKey !== state.lastResetKey) {
      return { hasError: false, error: undefined, lastResetKey: props.resetKey };
    }
    if (props.resetKey !== state.lastResetKey) {
      return { lastResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      "Visualization Error Boundary caught an error:",
      error,
      errorInfo,
    );
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  handleReset = () => {
    window.location.reload();
  };

  handleResetParams = () => {
    window.location.assign(window.location.pathname);
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const classified = classifyError(
        this.state.error as unknown as ErrorLike,
      );
      const { title, message } = getHumanReadableMessage(classified);
      const recovery = getRecoveryAction(classified);
      const onClick =
        recovery.action === "reset-params"
          ? this.handleResetParams
          : this.handleReset;

      return (
        <ErrorContainer>
          <ErrorTitle>{title}</ErrorTitle>
          <ErrorMessage>{message}</ErrorMessage>
          <ErrorButton variant="primary" size="lg" onClick={onClick}>
            {recovery.label}
          </ErrorButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default VisualizationErrorBoundary;
